/**
 * Success Step
 * Step 3: Account successfully connected
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { CheckCircle, ArrowRight, Plus, Mail01, File02, Folder } from "@untitledui/icons";

interface SuccessStepProps {
    onViewAccounts: () => void;
    onAddAnother: () => void;
}

export const SuccessStep = ({
    onViewAccounts,
    onAddAnother,
}: SuccessStepProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    const steps = [
        {
            icon: Mail01,
            text: t("success.step1"),
        },
        {
            icon: File02,
            text: t("success.step2"),
        },
        {
            icon: Folder,
            text: t("success.step3"),
        },
    ];

    return (
        <div className="space-y-6 text-center">
            {/* Success Icon */}
            <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-secondary">
                    <CheckCircle className="h-8 w-8 text-fg-success-secondary" />
                </div>
            </div>

            {/* Title */}
            <div>
                <h3 className="text-lg font-semibold text-primary">
                    {t("success.title")}
                </h3>
                <p className="mt-1 text-sm text-tertiary">
                    {t("success.description")}
                </p>
            </div>

            {/* What Happens Next */}
            <div className="space-y-4 rounded-lg bg-secondary p-4 text-left">
                <p className="text-sm font-medium text-primary">
                    {t("success.whatHappensNext")}
                </p>
                <ul className="space-y-3">
                    {steps.map((step, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-tertiary">
                            <step.icon className="mt-0.5 h-4 w-4 shrink-0 text-fg-brand-primary" />
                            <span>{step.text}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4">
                <Button
                    onClick={onViewAccounts}
                    color="primary"
                    size="md"
                    iconTrailing={ArrowRight}
                    className="w-full"
                >
                    {t("success.viewAccounts")}
                </Button>
                <Button
                    onClick={onAddAnother}
                    color="tertiary"
                    size="md"
                    iconLeading={Plus}
                    className="w-full"
                >
                    {t("success.addAnother")}
                </Button>
            </div>
        </div>
    );
};
